import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Avatar, Box, Button, Grid, Typography, useTheme } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import { BiTransfer } from 'react-icons/bi';
import clsx from 'clsx';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { HiOutlineLink } from 'react-icons/hi';
import { IoMapOutline } from 'react-icons/io5';
import { useHistory } from 'react-router-dom';
import { MdOutlinePeopleOutline } from 'react-icons/md';
import { Layout, Layouts } from 'react-grid-layout';
import { v4 as uuid } from 'uuid';
import { MdOutlinePersonOutline } from 'react-icons/md';
import { FormProvider } from 'react-hook-form';

import { TeamModuleContext, TeamsDashboardModuleContext, TeamsPageContext } from '@vyce/core/src/contexts';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { ConfirmDialog, EmptyList, CustomizeDashboard } from '@vyce/core/src/components';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { getFromLS, saveToLS } from '@vyce/core/src/utils/local-storage';
import defaultCompanyPicture from '@vyce/core/src/assets/company-icon.png';
import config from '@vyce/core/src/assets/config';
import { TransferEmployeesDialog } from './components';
import { TeamPublicLinkDialog } from '../teams/components/TeamPublicLinkDialog';
import useStyles from './styles';
import { useBooleanState } from '../../hooks';
import { EditTeamDialog } from './components/EditTeamDialog';
import { AppDashboard } from '../../components/Dashboard/AppDashboard';
import { useTeamsDashboardData } from '../../modules/teamsDashboardModule/hooks';
import { HealthSafetyWidget } from '@vyce/core/src/views/widgets/HealthSafetyWidget';
import { QualificationWidget } from '@vyce/core/src/views/widgets/QualificationWidget';
import { RecentlyRegisteredWidget } from '../../views/widgets/RecentlyRegisteredWidget';
import { TEAMS_PERMISSIONS, WidgetItem } from '../../types';
import { getRecentlyRegisteredRequest } from '../../api/dashboards';
import { DEFAULT_TEAM_DASHBOARD_LAYOUTS, sanitizeTeamDashboard } from './config';
import { TeamWorkersWidget } from '../widgets/TeamWorkersWidget';
import { AddManagerDialog } from './components/AddManagerDialog';
import { AssignLocationDialog, LocationMapDialog } from './components';
import { useAddNewSiteManager } from './hooks/useAddNewTeamManager';
import { ProximityToWorkWidget } from '../widgets/ProximityToWorkWidget';
import { ReadMore } from '../../components/ReadMore';

interface Props {
  isLegend?: boolean;
}

const LS_ITEMS_KEY = 'teamDashboardItems';
const LS_LAYOUT_KEY = 'teamDashboardLayouts';

export const TeamPage: React.FC<Props> = ({ isLegend = false }) => {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const horizontalScrollClasses = useHorizontalScrollStyles();
  const [isEditTeamDialogOpen, setEditTeamDialogOpen, setEditTeamDialogClose] = useBooleanState(false);
  const [isAddManagerDialogOpen, setAddManagerDialogOpen, setAddManagerDialogClose] = useBooleanState(false);
  const [isTeamDetailsVisible, , , toggleTeamDetails] = useBooleanState(false);
  const [isAssignLocationDialogOpen, setAssignLocationDialogOpen, setAssignLocationDialogClose] =
    useBooleanState(false);
  const [mapDialogLocation, setMapDialogLocation] = useState<{ id: string; name: string } | null>(null);
  const [signalForRerender, setSignalForRerender] = useState(uuid());

  const {
    open,
    logo,
    total,
    loading,
    teamData,
    pathname,
    setOpen,
    setOpenDeleteTeamDialog,
    openDeleteTeamDialog,
    publicLink,
    isPublicLinkDialogOpen,
    deleteTeam,
    closePublicLinkDialog,
    getStaffRequest,
    chatUser,
  } = useContext(TeamModuleContext);

  const { selectedCompanyId, selectedTeamIds } = useTeamsDashboardData({
    teamIds: teamData?.uuid ? [teamData.uuid] : undefined,
  });

  const {
    staff,
    saving,
    managers: teamManagers,
    methods,
    resetManagers,
    syncManagers,
    setStaffSubstring,
  } = useAddNewSiteManager({
    selectedCompanyId,
    teamId: teamData?.uuid ?? '',
    getStaffRequest,
    setSignalForRerender,
  });

  const [layouts, setLayouts] = useState<Layouts>(DEFAULT_TEAM_DASHBOARD_LAYOUTS);
  const [dashboard, setDashboard] = useState<WidgetItem[]>([]);

  const { mainDashboardFilters, widgetComponent } = useContext(TeamsDashboardModuleContext);

  const { inviteButton, onOpenUniversalInviteForm } = useContext(TeamsPageContext);
  const { canOnResource } = usePermissions();

  const teamLocations = useMemo(() => {
    return teamData?.location_ids?.map(locationId => {
      return mainDashboardFilters?.locations?.find(location => location.uuid === locationId);
    });
  }, [teamData?.location_ids, mainDashboardFilters?.locations]);

  // Rights are per team — the same person may manage one team and not the next.
  const canEditTeam = canOnResource(TEAMS_PERMISSIONS.CAN_EDIT_TEAMS, 'team', teamData?.uuid);
  const canDeleteTeam = canOnResource(TEAMS_PERMISSIONS.CAN_DELETE_TEAMS, 'team', teamData?.uuid);
  const canAddMembers = canOnResource(TEAMS_PERMISSIONS.CAN_ADD_TEAM_MEMBERS, 'team', teamData?.uuid);
  const canRemoveMembers = canOnResource(TEAMS_PERMISSIONS.CAN_REMOVE_TEAM_MEMBERS, 'team', teamData?.uuid);

  const options = [
    {
      id: '1',
      icon: <BiPencil size="16px" color="#610BEF" />,
      bgColor: theme.palette.primary.light,
      callback: () => setEditTeamDialogOpen(),
      show: canEditTeam,
    },
    {
      id: '2',
      icon: <HiOutlineLink size="16px" color="#005BD4" />,
      bgColor: theme.palette.type === 'dark' ? '#50C8FC' : '#E3FEFF',
      callback: () => {
        history.push(`${pathname}/sync_settings`);
      },
      show: canEditTeam,
    },

    {
      id: '3',
      icon: <BiTrash size="16px" color="#E61B00" />,
      bgColor: theme.palette.type === 'dark' ? '#FF75CB' : '#FFF2F1',
      callback: () => {
        setOpenDeleteTeamDialog(true);
      },
      show: canDeleteTeam,
    },
  ];

  const getWidget = useCallback(
    (widget: WidgetItem) => {
      switch (widget.system_name) {
        case 'team_workers':
          return {
            component: TeamWorkersWidget,
            props: {
              selectedCompanyId,
              signalForRerender,
              teamIds: teamData?.uuid ? [teamData.uuid] : undefined,
            },
          };
        case 'team_health_and_safety':
          return {
            component: HealthSafetyWidget,
            props: { companyId: selectedCompanyId, teamIds: teamData?.uuid ? [teamData.uuid] : undefined },
          };
        case 'teams_qualifications':
          return {
            component: QualificationWidget,
            props: {
              selectedCompanyId,
              mainDashboardFilters: mainDashboardFilters,
              customTeamId: teamData?.uuid ? teamData.uuid : undefined,
              showTeamsSelector: false,
              chatUser,
            },
          };
        case 'proximity_to_work':
          return {
            component: ProximityToWorkWidget,
            props: {
              selectedCompanyId,
              mainDashboardFilters: mainDashboardFilters,
            },
          };
        case 'recent_registered_employees':
          return {
            component: RecentlyRegisteredWidget,
            props: {
              selectedCompanyId,
              mainDashboardFilters: mainDashboardFilters,
              moduleType: 'teams',
              fetchRecentlyRegisteredWorkers: getRecentlyRegisteredRequest,
              customTeamId: teamData?.uuid ? teamData.uuid : undefined,
              chatUser,
            },
          };

        default:
          return {
            component: widgetComponent,
            props: {
              widget,
              redirectUrl: '/teams/members',
              mainDashboardFilters: mainDashboardFilters,
              customTeamId: teamData?.uuid ? teamData.uuid : undefined,
            },
          };
      }
    },
    [selectedCompanyId, selectedTeamIds, mainDashboardFilters, signalForRerender]
  );

  const resetDashboards = () => {
    if (dashboard && teamData?.uuid) {
      const restoredDashboard = dashboard.map(item => {
        return { ...item, is_active: true };
      });
      setDashboard(restoredDashboard);
      saveToLS(LS_ITEMS_KEY, restoredDashboard);
    }
  };

  const onWidgetsChangeViaSettings = (newWidget: WidgetItem[]) => {
    setDashboard(newWidget);
    if (teamData?.uuid) {
      saveToLS(LS_ITEMS_KEY, newWidget);
    }
  };

  const onLayoutChange = (currentLayout: Layout[], newLayouts: Layouts) => {
    setLayouts(newLayouts);
    if (teamData?.uuid) {
      saveToLS(LS_LAYOUT_KEY, newLayouts);
    }
  };

  const handleAddManagerDialogClose = () => {
    resetManagers();
    setAddManagerDialogClose();
  };

  const handleAddManagers = async () => {
    await syncManagers();
    setAddManagerDialogClose();
  };

  const onWidgetStatusChange = (systemName: string) => {
    const newWidgets: WidgetItem[] = dashboard.map(item =>
      item.system_name === systemName ? { ...item, is_active: !item.is_active } : item
    );
    setDashboard(newWidgets);
    if (teamData?.uuid) {
      saveToLS(LS_ITEMS_KEY, newWidgets);
    }
  };

  useEffect(() => {
    if (teamData?.uuid) {
      const savedWidgets = getFromLS(LS_ITEMS_KEY);
      const savedLayouts = getFromLS(LS_LAYOUT_KEY);

      const { items, layouts: cleanLayouts } = sanitizeTeamDashboard(savedWidgets, savedLayouts);

      setDashboard(items);
      saveToLS(LS_ITEMS_KEY, items);

      setLayouts(cleanLayouts);
      saveToLS(LS_LAYOUT_KEY, cleanLayouts);
    }
  }, [teamData?.uuid]);
  return (
    <>
      <FormProvider {...methods}>
        {/* div, not form: widgets render their own <form>, which can't be nested in a <form> */}
        <div>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            className={classes.controlBlock}>
            <Box display="flex" alignItems="center" gridGap={16}>
              <Avatar src={logo?.url}>
                {!logo?.url && <img height={40} alt="company" src={defaultCompanyPicture} />}
              </Avatar>
              <Box>
                <Typography variant="h6" className={classes.companyName}>
                  {teamData?.name}
                </Typography>

                <Box display="flex" gridGap={8} alignItems="center">
                  <MdOutlinePeopleOutline size="18px" color={theme.palette.text.secondary} />
                  <Typography variant="subtitle2" color="textSecondary">
                    {total} Members
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box display="flex" gridGap={16} alignItems="center">
              {teamData && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={toggleTeamDetails}
                  cy-test-id="hide-show-team-details-button">
                  {isTeamDetailsVisible ? 'Hide' : 'Show'} Team Details
                </Button>
              )}
              {options.map(({ show, id, icon, bgColor, callback }) =>
                show ? (
                  <Button
                    key={id}
                    startIcon={icon}
                    className={classes.buttonWrapper}
                    style={{ backgroundColor: bgColor }}
                    onClick={callback}
                  />
                ) : null
              )}
            </Box>
          </Box>

          {teamData && isTeamDetailsVisible && (
            <Box className={classes.teamDetailsBlock} marginTop={3}>
              <Box>
                {(teamData.description || '').trim() ? (
                  <Box mb={2}>
                    <ReadMore lines={3}>{teamData.description?.trim() || '—'}</ReadMore>
                  </Box>
                ) : null}

                <Box>
                  <Box className={classes.linkedLocationsHeader}>
                    <Typography className={classes.linkedLocationsTitle}>Linked Locations:</Typography>
                    {canEditTeam && (
                      <Button
                        variant="outlined"
                        size="small"
                        className={classes.assignLocationButton}
                        startIcon={<AiOutlinePlus size="16px" />}
                        onClick={setAssignLocationDialogOpen}>
                        Assign Location
                      </Button>
                    )}
                  </Box>

                  {teamLocations && teamLocations.length > 0 ? (
                    <Box display="flex" flexDirection="column">
                      {teamLocations?.map(loc => (
                        <Box key={loc?.uuid} className={classes.locationCard}>
                          <Typography className={classes.locationCardLabel}>Location Name:</Typography>
                          <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
                            <Typography className={classes.locationCardName}>{loc?.name}</Typography>
                            {(loc as any).address && (
                              <Typography className={classes.locationCardAddress}>
                                {[
                                  (loc as any).address.address_line_1,
                                  (loc as any).address.address_line_2,
                                  (loc as any).address.address_line_3,
                                  (loc as any).address.address_line_4,
                                ]
                                  .filter(Boolean)
                                  .join(', ')}
                              </Typography>
                            )}
                          </Box>
                          <Button
                            className={classes.locationCardMapButton}
                            disableElevation
                            onClick={() =>
                              loc?.uuid && setMapDialogLocation({ id: loc.uuid, name: loc.name })
                            }>
                            <IoMapOutline size="16px" color="#610BEF" />
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No linked locations
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Box marginTop={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  gridGap={16}
                  mt={3}
                  className={clsx(
                    horizontalScrollClasses.blockWrapper,
                    horizontalScrollClasses.blockWithHideScroll
                  )}>
                  <Box />

                  <Box display="flex" gridGap={16}>
                    {canEditTeam && (
                      <Button
                        color="primary"
                        size="small"
                        cy-test-id="add-existing-users-to-team-button"
                        onClick={setAddManagerDialogOpen}
                        startIcon={<MdOutlinePersonOutline />}
                        variant="outlined">
                        Add a Manager
                      </Button>
                    )}
                    {canAddMembers && (
                      <Button
                        color="secondary"
                        size="small"
                        cy-test-id="add-existing-users-to-team-button"
                        onClick={() => setOpen(true)}
                        startIcon={<BiTransfer />}
                        variant="contained">
                        Add existing Teams users
                      </Button>
                    )}

                    {inviteButton ? (
                      React.createElement(inviteButton)
                    ) : (
                      <Button
                        onClick={onOpenUniversalInviteForm}
                        size="small"
                        data-tour="invite"
                        startIcon={<AiOutlinePlus />}
                        variant="contained"
                        color="primary">
                        Invite New Members
                      </Button>
                    )}

                    <CustomizeDashboard
                      widgetItems={dashboard}
                      companyId={selectedCompanyId ?? ''}
                      teamPermission={false}
                      timePermission={false}
                      mainDashboardFiltersFromState={mainDashboardFilters}
                      onWidgetsChangeViaSettings={onWidgetsChangeViaSettings}
                      resetDashboards={resetDashboards}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                {!teamData && !loading ? (
                  <EmptyList
                    image={config.icons3D.lightning}
                    title="We haven't found any team members."
                    subtitle="Please add or invite team members to see data about you teams."
                  />
                ) : (
                  <Box style={{ marginRight: '-24px', marginLeft: '-24px' }}>
                    <AppDashboard
                      layouts={layouts}
                      onLayoutChange={onLayoutChange}
                      widgets={dashboard || []}
                      getWidget={getWidget}
                      onWidgetStatusChange={onWidgetStatusChange}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          {teamData?.uuid && (
            <TransferEmployeesDialog
              open={open}
              setOpen={setOpen}
              setSignalForRerender={setSignalForRerender}
            />
          )}

          {teamData?.uuid && (
            <AddManagerDialog
              staff={staff}
              setSubstring={setStaffSubstring}
              open={isAddManagerDialogOpen}
              onClose={handleAddManagerDialogClose}
              setSignalForRerender={setSignalForRerender}
              onAddManagers={handleAddManagers}
              teamManagers={teamManagers}
              saving={saving}
            />
          )}

          <ConfirmDialog
            handleClose={() => setOpenDeleteTeamDialog(false)}
            open={openDeleteTeamDialog}
            confirmText="Delete"
            cancelText="Cancel"
            title={`Are you sure you want to delete ${teamData?.name} team?`}
            handleConfirm={deleteTeam}
            loading={loading}
          />

          {isEditTeamDialogOpen && (
            <EditTeamDialog isOpen={isEditTeamDialogOpen} onClose={setEditTeamDialogClose} />
          )}

          <AssignLocationDialog
            open={isAssignLocationDialogOpen}
            onClose={setAssignLocationDialogClose}
            locations={mainDashboardFilters?.locations ?? []}
            selectedLocationIds={teamData?.location_ids ?? []}
            onSuccess={() => setSignalForRerender(uuid())}
          />

          {mapDialogLocation && selectedCompanyId && (
            <LocationMapDialog
              open={!!mapDialogLocation}
              onClose={() => setMapDialogLocation(null)}
              locationId={mapDialogLocation.id}
              locationName={mapDialogLocation.name}
              companyId={selectedCompanyId}
            />
          )}

          {publicLink && closePublicLinkDialog && (
            <TeamPublicLinkDialog
              link={publicLink}
              open={!!isPublicLinkDialogOpen}
              handleClose={closePublicLinkDialog}
            />
          )}
        </div>
      </FormProvider>
    </>
  );
};
